import { auth } from "../../firebase/config";
import storeDB from "../indexedDB/storeDB";
import { isIssuedDeviceId, publishStationDevice, readWorkstationDeviceId } from "../workstationDevice";

const storageKey = (storeId) => `workstationOrigin_${storeId}`;
const identityKeys = ["stationId", "stationName", "stationDocumentId", "deviceId"];

let memory = {
  storeId: "",
  stationId: "",
  stationName: "",
  stationDocumentId: "",
  deviceId: "",
};

function readStored(storeId) {
  try {
    const raw = localStorage.getItem(storageKey(storeId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStored(storeId) {
  try {
    localStorage.setItem(storageKey(storeId), JSON.stringify({
      stationId: memory.stationId,
      stationName: memory.stationName,
      stationDocumentId: memory.stationDocumentId,
      deviceId: memory.deviceId,
    }));
  } catch {
    // Memory still has the station for this session.
  }
}

function loadMemory(storeId) {
  if (!storeId || memory.storeId === storeId) return;
  const stored = readStored(storeId);
  memory = {
    storeId,
    stationId: String(stored.stationId || ""),
    stationName: String(stored.stationName || ""),
    stationDocumentId: String(stored.stationDocumentId || ""),
    deviceId: String(stored.deviceId || ""),
  };
}

export function rememberWorkstation(storeId, fields = {}) {
  if (!storeId) return;
  loadMemory(storeId);
  let changed = false;
  identityKeys.forEach((key) => {
    const value = String(fields[key] || "").trim();
    if (value && memory[key] !== value) {
      memory[key] = value;
      changed = true;
    }
  });
  if (changed) writeStored(storeId);
}

export function replaceWorkstationId(storeId, stationId) {
  if (!storeId) return;
  loadMemory(storeId);
  memory.stationId = String(stationId || "");
  writeStored(storeId);
}

export function currentPosUser(storeId) {
  return readPosUser(storeId);
}

function readPosUser(storeId) {
  if (!storeId) return null;
  try {
    const raw = localStorage.getItem(`currentPOSUser_${storeId}`);
    if (!raw) return null;
    const user = JSON.parse(raw);
    if (!user?.id) return null;
    return {
      id: String(user.id),
      name: String(user.name || "").trim(),
      role: user.role ? String(user.role) : "",
    };
  } catch {
    return null;
  }
}

async function saveDeviceLocally(storeId, deviceId) {
  if (!storeId || !deviceId) return;
  try {
    await storeDB.init();
    await storeDB.saveWorkstationDevice(storeId, deviceId);
  } catch (error) {
    console.warn("Device ID was not saved on this computer:", error);
  }
}

export async function refreshWorkstationDevice(storeId) {
  if (!storeId) return "";
  loadMemory(storeId);
  let deviceId = await readWorkstationDeviceId(storeId).catch(() => "");
  try {
    await storeDB.init();
    const local = await storeDB.getStationId(storeId);
    if (local?.assigned === true && local.stationId) {
      rememberWorkstation(storeId, {
        stationId: local.stationId,
        stationName: local.stationName || "",
      });
    }
    if (!deviceId && isIssuedDeviceId(local?.deviceId)) deviceId = local.deviceId;
  } catch {
    deviceId = deviceId || "";
  }
  if (deviceId && deviceId !== memory.deviceId) {
    rememberWorkstation(storeId, { deviceId });
    await saveDeviceLocally(storeId, deviceId);
  }
  return memory.deviceId || "";
}

export async function rememberIssuedDevice(storeId, deviceId) {
  if (!storeId || !isIssuedDeviceId(deviceId)) return;
  rememberWorkstation(storeId, { deviceId });
  await saveDeviceLocally(storeId, deviceId);
  let stationId = memory.storeId === storeId ? memory.stationId : "";
  if (!stationId) {
    try {
      await storeDB.init();
      const local = await storeDB.getStationId(storeId);
      if (local?.assigned === true && local.stationId) {
        stationId = local.stationId;
        rememberWorkstation(storeId, { stationId, stationName: local.stationName || "" });
      }
    } catch {
      stationId = "";
    }
  }
  if (!stationId) return;
  try {
    await publishStationDevice(storeId, stationId, deviceId);
  } catch (error) {
    console.warn("Device ID was not saved on the station record:", error);
  }
}

export function actionStamp(store, action) {
  const storeId = store?.id || "";
  loadMemory(storeId);
  const posUser = readPosUser(storeId);
  return {
    action: String(action || ""),
    stationId: memory.stationId || null,
    stationName: memory.stationName || null,
    deviceId: memory.deviceId || null,
    posUserId: posUser?.id || null,
    posUserName: posUser?.name || null,
    at: Date.now(),
  };
}

export async function parkOriginPatch(storeId, existing) {
  await refreshWorkstationDevice(storeId);
  loadMemory(storeId);
  const posUser = readPosUser(storeId);
  const prior = existing && typeof existing === "object" ? existing : {};
  const who = {
    stationId: memory.stationId || null,
    stationName: memory.stationName || null,
    deviceId: memory.deviceId || null,
    posUserId: posUser?.id || null,
    posUserName: posUser?.name || null,
  };
  const origin = prior.stationId ? {
    stationId: prior.stationId,
    stationName: prior.stationName || null,
    deviceId: prior.deviceId || (prior.stationId === who.stationId ? who.deviceId : null),
    posUserId: prior.posUserId || null,
    posUserName: prior.posUserName || null,
  } : who;
  const action = { action: "park", ...who, at: Date.now() };
  return {
    ...origin,
    orderActions: [...(Array.isArray(prior.orderActions) ? prior.orderActions : []), action],
  };
}

export function buildOrderOrigin(store) {
  const storeId = store?.id || "";
  loadMemory(storeId);
  const stationId = memory.stationId || "";
  const stationName = memory.stationName || "";
  if (storeId) {
    rememberWorkstation(storeId, {
      stationId,
      stationName,
      stationDocumentId: memory.stationDocumentId,
      deviceId: memory.deviceId,
    });
  }
  const posUser = readPosUser(storeId);
  const loginUser = auth.currentUser;
  return {
    stationId: stationId || null,
    stationName: stationName || null,
    stationDocumentId: memory.stationDocumentId || null,
    deviceId: memory.deviceId || null,
    posUserId: posUser?.id || null,
    posUserName: posUser?.name || null,
    loginUserId: loginUser?.uid || null,
    loginUserEmail: loginUser?.email || null,
    loginUserName: loginUser?.displayName || null,
  };
}
